// src/components/DownloadOverlay.js
import React, { useState } from 'react';

export default function DownloadOverlay({
  visible,
  onClose,
  onConfirm,     // called after we attempt to mark-downloaded (success or fail)
  username,      // e.g. email.split('@')[0]
  project,       // e.g. base filename without extension
  extra,         // optional object merged into the payload
  credits = 1    // number of credits to be deducted (1 for STLGenerator, 3 for custom orders)
}) {
  const [submitting, setSubmitting] = useState(false);
  const creditLabel = `${credits} Credit${credits === 1 ? '' : 's'}`;
  const handleClickOutside = (e) => {
    if (e.target.id === 'download-overlay-backdrop') onClose?.();
  };

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      console.log('[DownloadOverlay] confirm clicked; username=', username, 'project=', project);

      if (!username || !project) {
        console.warn('[DownloadOverlay] Missing username/project — skipping POST to /mark-downloaded/.');
      } else {
        const res = await fetch('/mark-downloaded/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            project,
            event: 'confirm_download',
            ts: new Date().toISOString(),
            credits,
            ...(extra || {})
          })

        });

        // Read response text to expose backend errors in the console if any.
        const bodyText = await res.text().catch(() => '');
        if (!res.ok) {
          console.error('[DownloadOverlay] /mark-downloaded/ failed:', res.status, bodyText);
        } else {
          console.log('[DownloadOverlay] /mark-downloaded/ ok:', res.status, bodyText);
        }
      }
    } catch (err) {
      console.error('[DownloadOverlay] Network error while posting /mark-downloaded/:', err);
    } finally {
      setSubmitting(false);
      onConfirm?.(); // continue flow regardless (download, etc.)
    }
  };

  // Guard AFTER hooks are declared so hooks aren't conditional.
  if (!visible) return null;

  return (
    <div
      id="download-overlay-backdrop"
      onClick={handleClickOutside}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(2px)'
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-overlay-title"
        style={{
          background: 'white',
          padding: '1.2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          textAlign: 'center',
          width: 'min(90vw, 520px)',
          maxWidth: '520px',
          maxHeight: 'min(90vh, 520px)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '10px',
            right: '14px',
            background: 'transparent',
            border: 'none',
            fontSize: '1.25rem',
            lineHeight: 1,
            cursor: 'pointer'
          }}
        >
          ✖
        </button>

        <h2 id="download-overlay-title" style={{ margin: 0, fontSize: '0' }}>
          {/* Visually hidden; keeps a11y title without changing layout */}
          .
        </h2>

        {/* Content area that wraps & scrolls if needed */}
        <div
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            padding: '0.75rem 0.5rem',
            marginTop: '0.5rem',
            marginBottom: '1rem',
            fontSize: 'clamp(14px, 5vw, 18px)',
            fontWeight: 600,
            color: '#1e293b',
            textAlign: 'center',
            lineHeight: 1.35,
            whiteSpace: 'normal',        // allow wrapping
            wordBreak: 'normal',         // prevent breaking inside words
            overflowWrap: 'normal',      // same: don’t break inside words
            hyphens: 'none'              // disable automatic hyphenation
          }}
        >
          {creditLabel} will be deducted for this action.<br />
          Once completed, you may find and edit your project in the “My Projects” tab{' '}
          <u>without using any more credits.</u>
        </div>


        <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            style={{
              padding: '10px 20px',
              fontSize: '1rem',
              fontWeight: 'bold',
              backgroundColor: submitting ? '#7cc7eb' : '#0ea5e9',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Confirming…' : 'Confirm Download'}
          </button>
        </div>
      </div>
    </div>
  );
}
